if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          previous: this.querySelector('[data-product-gallery-previous]'),
          next: this.querySelector('[data-product-gallery-next]'),
          viewerPrevious: this.querySelector('[id^="GalleryViewer"] > .slider-buttons button[name="previous"]'),
          viewerNext: this.querySelector('[id^="GalleryViewer"] > .slider-buttons button[name="next"]'),
          thumbnailPrevious: this.querySelector('[id^="GalleryThumbnails"] > button[name="previous"]'),
          thumbnailNext: this.querySelector('[id^="GalleryThumbnails"] > button[name="next"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // MediaGallery is the single navigation state owner. Dawn's SliderComponent keeps
        // native scroll observation so touch/trackpad gestures can still emit slideChanged.
        if (this.elements.viewer) this.elements.viewer.enableSliderLooping = true;
        if (this.elements.thumbnails) this.elements.thumbnails.enableSliderLooping = true;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.previous?.addEventListener('click', () => this.stepMedia(-1));
        this.elements.next?.addEventListener('click', () => this.stepMedia(1));
        this.elements.viewerPrevious?.addEventListener(
          'click',
          this.onGalleryControlClick.bind(this, -1),
          { capture: true }
        );
        this.elements.viewerNext?.addEventListener(
          'click',
          this.onGalleryControlClick.bind(this, 1),
          { capture: true }
        );
        this.elements.thumbnailPrevious?.addEventListener(
          'click',
          this.onGalleryControlClick.bind(this, -1),
          { capture: true }
        );
        this.elements.thumbnailNext?.addEventListener(
          'click',
          this.onGalleryControlClick.bind(this, 1),
          { capture: true }
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.updateGalleryControls();
      }

      getMediaItems() {
        return [
          ...this.elements.viewer.querySelectorAll('.product__media-list > .product__media-item[data-media-id]'),
        ];
      }

      onGalleryControlClick(direction, event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.stepMedia(direction);
      }

      onSlideChanged(event) {
        const currentMedia = event.detail.currentElement;
        if (currentMedia?.matches?.('.product__media-item[data-media-id]')) {
          this.getMediaItems().forEach((element) => {
            element.classList.toggle('is-active', element === currentMedia);
          });
        }

        this.updateGalleryControls();
        if (!this.elements.thumbnails || !currentMedia?.matches?.('.product__media-item[data-media-id]')) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      stepMedia(direction) {
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const foundIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const activeIndex = foundIndex >= 0 ? foundIndex : 0;
        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;

        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      updateGalleryControls() {
        const mediaItems = this.getMediaItems();
        const foundIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const activeIndex = foundIndex >= 0 ? foundIndex : 0;

        const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
        const totalCounter = this.elements.viewer.querySelector('.slider-counter--total');
        if (currentCounter) currentCounter.textContent = mediaItems.length ? String(activeIndex + 1) : '0';
        if (totalCounter) totalCounter.textContent = String(mediaItems.length);

        const navigationDisabled = mediaItems.length < 2;
        [
          this.elements.previous,
          this.elements.next,
          this.elements.viewerPrevious,
          this.elements.viewerNext,
          this.elements.thumbnailPrevious,
          this.elements.thumbnailNext,
        ].forEach((button) => {
          if (!button) return;
          button.disabled = navigationDisabled;
          button.setAttribute('aria-disabled', String(navigationDisabled));
        });
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = this.getMediaItems();
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) || mediaItems[0];
        if (!activeMedia) return;

        mediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.updateGalleryControls();

        window.setTimeout(() => {
          const usesSingleMediaDesktopLayout =
            this.mql.matches && this.dataset.desktopLayout.includes('thumbnail');

          if (usesSingleMediaDesktopLayout) {
            activeMedia.parentElement.scrollTo({ left: 0 });
          } else {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll the page if the image is already in view.
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
